import * as React from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/useToast';

interface CopyableIdProps {
  value: string;
  display?: string;
  label?: string;
  iconOnly?: boolean;
  className?: string;
}

export function CopyableId({ value, display, label = 'ID', iconOnly, className }: CopyableIdProps) {
  const [copied, setCopied] = React.useState(false);

  const onCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    void navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        toast({ title: `${label} copied` });
        window.setTimeout(() => setCopied(false), 1200);
      })
      .catch(() => {
        // Safari/Firefox can reject without permission, e.g. on insecure
        // origins or when the document is not focused.
        toast({
          title: 'Copy failed',
          description: 'Clipboard access was denied.',
          variant: 'destructive',
        });
      });
  };

  return (
    <button
      type="button"
      onClick={onCopy}
      title={`Copy ${label}: ${value}`}
      aria-label={`Copy ${label}`}
      className={cn(
        'inline-flex items-center gap-1 rounded border border-transparent px-1.5 py-0.5 font-mono text-xs text-muted-foreground hover:border-border hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        className,
      )}
    >
      {!iconOnly && <span className="truncate">{display ?? value}</span>}
      {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3 opacity-60" />}
    </button>
  );
}
